import { useState, useEffect, useRef, useCallback } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import type { SavedLesson } from '@/mocks/lessons';

interface LessonPlayerProps {
  lesson: SavedLesson;
  onPlayStateChange?: (playing: boolean) => void;
}

const speedOptions = [0.5, 0.75, 1, 1.25, 1.5, 2];
const qualityOptions = ['Auto', '1080p', '720p', '480p', '360p'];
const voiceOptions = ['LAVYN (Default)', 'Teacher Mode', 'Motivational', 'Calm & Steady'];

const formatTime = (t: number) => {
  const m = Math.floor(t / 60);
  const s = Math.floor(t % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

const DOUBLE_TAP_MS = 280;
const DOUBLE_TAP_DISTANCE = 70;
const PLAY_FLASH_MS = 650;
const CONTROLS_HIDE_DELAY = 3000;

/* ── Orientation lock ── */
const lockLandscape = async () => {
  try {
    const orient = (screen as unknown as { orientation?: { lock?: (o: string) => Promise<void>; unlock?: () => void } }).orientation;
    if (orient?.lock) await orient.lock('landscape');
  } catch { /* silently fail */ }
};
const unlockOrientation = () => {
  try {
    const orient = (screen as unknown as { orientation?: { unlock?: () => void } }).orientation;
    if (orient?.unlock) orient.unlock();
  } catch { /* silently fail */ }
};

export default function LessonPlayer({ lesson, onPlayStateChange }: LessonPlayerProps) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  /* ── State ── */
  const [isPlaying, setIsPlaying] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [showCaptions, setShowCaptions] = useState(true);
  const [isMuted, setIsMuted] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [videoQuality, setVideoQuality] = useState('Auto');
  const [autoPlay, setAutoPlay] = useState(false);
  const [aiVoice, setAiVoice] = useState('LAVYN (Default)');
  const [currentTime, setCurrentTime] = useState(0);
  const [volume, setVolume] = useState(80);
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [zoomScale, setZoomScale] = useState(1);
  const [zoomOrigin, setZoomOrigin] = useState({ x: 50, y: 50 });
  const [seekFlash, setSeekFlash] = useState<{ side: 'left' | 'right' | null; label: string }>({ side: null, label: '' });
  const [playFlash, setPlayFlash] = useState<'play' | 'pause' | null>(null);
  const [isDraggingProgress, setIsDraggingProgress] = useState(false);
  const [seekTooltip, setSeekTooltip] = useState<{ visible: boolean; time: number; x: number }>({ visible: false, time: 0, x: 0 });

  /* ── Refs ── */
  const videoContainerRef = useRef<HTMLDivElement>(null);
  const progressBarRef = useRef<HTMLDivElement>(null);
  const controlsTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const seekFlashTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const playFlashTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const volTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isPlayingRef = useRef(isPlaying);
  isPlayingRef.current = isPlaying;
  const showSettingsRef = useRef(showSettings);
  showSettingsRef.current = showSettings;
  const showVolumeSliderRef = useRef(showVolumeSlider);
  showVolumeSliderRef.current = showVolumeSlider;
  const isDraggingRef = useRef(false);

  const tapRef = useRef<{
    x: number; y: number; time: number; moved: boolean;
    doubleTap: { lastTime: number; lastX: number; lastY: number; lastZone: 'left' | 'right' | null };
  }>({
    x: 0, y: 0, time: 0, moved: false,
    doubleTap: { lastTime: 0, lastX: 0, lastY: 0, lastZone: null },
  });

  const pinchRef = useRef<{
    initialDist: number; initialScale: number; centerX: number; centerY: number;
  } | null>(null);

  const { duration, narratorScript } = lesson;

  const currentSubtitle = narratorScript
    .slice().reverse()
    .find((s) => currentTime >= s.time);
  const subtitleText = currentSubtitle?.text || '';

  /* ── Theme tokens ── */
  const t = {
    videoBg: isDark ? 'bg-[#0A0A0B]' : 'bg-neutral-900',
    fullscreenBg: isDark ? 'bg-black' : 'bg-white',
    ctrlBg: isDark ? 'bg-black/40' : 'bg-white/30',
    ctrlBgActive: isDark ? 'bg-white/20' : 'bg-black/20',
    ctrlBorder: isDark ? 'border-white/[0.08]' : 'border-black/10',
    ctrlText: isDark ? 'text-white/70' : 'text-black/60',
    ctrlTextHover: isDark ? 'text-white' : 'text-black',
    ctrlIcon: isDark ? 'text-white/70' : 'text-black/60',
    overlay: isDark ? 'bg-gradient-to-t from-black/80 via-black/30 to-transparent' : 'bg-gradient-to-t from-black/60 via-black/20 to-transparent',
    overlayFullscreen: isDark ? 'bg-black/40' : 'bg-black/20',
    textPrimary: isDark ? 'text-white' : 'text-neutral-900',
    textSecondary: isDark ? 'text-white/50' : 'text-black/50',
    textMuted: isDark ? 'text-white/30' : 'text-black/30',
    textFaint: isDark ? 'text-white/20' : 'text-black/20',
    textBrand: isDark ? 'text-white/40' : 'text-black/40',
    progressTrack: isDark ? 'bg-white/[0.12]' : 'bg-black/10',
    progressFill: isDark ? 'bg-white' : 'bg-black',
    progressBuffered: isDark ? 'bg-white/[0.06]' : 'bg-black/5',
    btnPrimary: isDark ? 'bg-white text-black' : 'bg-black text-white',
    btnGhost: isDark ? 'bg-white/[0.08] border-white/[0.12]' : 'bg-black/[0.06] border-black/[0.08]',
    brandText: isDark ? 'text-white/40' : 'text-black/40',
    ccOn: isDark ? 'bg-white/20 border-white/20 text-white' : 'bg-black/20 border-black/20 text-black',
    ccOff: isDark ? 'bg-black/40 border-white/[0.08] text-white/50' : 'bg-white/30 border-black/10 text-black/50',
    subtitleBg: isDark ? 'bg-black/50' : 'bg-white/70',
    subtitleText: isDark ? 'text-white' : 'text-black',
    sheetBg: isDark ? 'bg-[#161618]' : 'bg-white',
    sheetBackdrop: isDark ? 'bg-black/70' : 'bg-black/40',
    settingsLabel: isDark ? 'text-white/50' : 'text-black/50',
    settingsOption: isDark ? 'text-white/50 hover:text-white/70 hover:bg-white/[0.04]' : 'text-black/50 hover:text-black/70 hover:bg-black/[0.04]',
    settingsOptionActive: isDark ? 'bg-white text-black' : 'bg-black text-white',
    settingsOptionInactive: isDark ? 'bg-white/[0.04] text-white/40 hover:bg-white/[0.08]' : 'bg-black/[0.04] text-black/40 hover:bg-black/[0.08]',
    toggleTrackOn: isDark ? 'bg-white' : 'bg-black',
    toggleTrackOff: isDark ? 'bg-white/[0.12]' : 'bg-black/[0.12]',
    toggleThumb: isDark ? 'bg-black' : 'bg-white',
    settingsBorder: isDark ? 'border-white/[0.04]' : 'border-black/[0.04]',
    settingsIcon: isDark ? 'text-white/40' : 'text-black/40',
    settingsIconMuted: isDark ? 'text-white/20' : 'text-black/20',
    seekFlashBg: isDark ? 'bg-black/60 border-white/10' : 'bg-white/70 border-black/10',
    seekFlashText: isDark ? 'text-white' : 'text-black',
    seekFlashLabelBg: isDark ? 'bg-black/50' : 'bg-white/70',
    playFlashBg: isDark ? 'bg-black/50' : 'bg-white/70',
    playFlashIcon: isDark ? 'text-white' : 'text-black',
    bottomBarBg: isDark ? 'bg-gradient-to-t from-black/80 via-black/30 to-transparent' : 'bg-gradient-to-t from-black/60 via-black/20 to-transparent',
    bottomBarBgFullscreen: isDark ? 'bg-black/40' : 'bg-black/20',
    playBtnBg: isDark ? 'bg-white/[0.08] border-white/[0.12]' : 'bg-black/[0.08] border-black/[0.12]',
    playBtnIcon: isDark ? 'text-white' : 'text-black',
    timeText: isDark ? 'text-white/50' : 'text-black/50',
    speedText: isDark ? 'text-white/30' : 'text-black/30',
    externalTrack: isDark ? 'bg-white/[0.06]' : 'bg-black/[0.06]',
    muteIcon: isDark ? 'text-red-400' : 'text-red-500',
    volHigh: isDark ? 'text-white' : 'text-black',
    volLow: isDark ? 'text-white/80' : 'text-black/70',
    tooltipBg: isDark ? 'bg-[#1C1D21]' : 'bg-white',
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  /* ── Helpers ── */
  const showControlsAndResetTimer = useCallback(() => {
    setShowControls(true);
    if (controlsTimerRef.current) clearTimeout(controlsTimerRef.current);
    if (!isPlayingRef.current) return; // keep visible when paused
    controlsTimerRef.current = setTimeout(() => {
      if (isPlayingRef.current && !showSettingsRef.current && !showVolumeSliderRef.current) {
        setShowControls(false);
      }
    }, CONTROLS_HIDE_DELAY);
  }, []);

  const showPlayFlash = (action: 'play' | 'pause') => {
    if (playFlashTimeoutRef.current) clearTimeout(playFlashTimeoutRef.current);
    setPlayFlash(action);
    playFlashTimeoutRef.current = setTimeout(() => setPlayFlash(null), PLAY_FLASH_MS);
  };

  const showSeekFlash = (side: 'left' | 'right', label: string) => {
    if (seekFlashTimeoutRef.current) clearTimeout(seekFlashTimeoutRef.current);
    setSeekFlash({ side, label });
    seekFlashTimeoutRef.current = setTimeout(() => setSeekFlash({ side: null, label: '' }), 700);
  };

  const togglePlay = useCallback(() => {
    setIsPlaying((p) => {
      const next = !p;
      showPlayFlash(next ? 'play' : 'pause');
      onPlayStateChange?.(next);
      return next;
    });
  }, [onPlayStateChange]);

  const handleSeek = useCallback((clientX: number, el?: HTMLDivElement) => {
    const bar = el || progressBarRef.current;
    if (!bar) return;
    const rect = bar.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    setCurrentTime(pct * duration);
  }, [duration]);

  const getTapZone = (clientX: number, rect: DOMRect): 'left' | 'center' | 'right' => {
    const rx = (clientX - rect.left) / rect.width;
    if (rx < 0.33) return 'left';
    if (rx > 0.67) return 'right';
    return 'center';
  };

  /* ── Playback timer ── */
  useEffect(() => {
    if (!isPlaying) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      return;
    }
    intervalRef.current = setInterval(() => {
      setCurrentTime((t) => {
        const next = t + 1 * playbackSpeed;
        if (next >= duration) { setIsPlaying(false); return duration; }
        return next;
      });
    }, 1000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [isPlaying, playbackSpeed, duration]);

  /* ── Controls auto-hide ── */
  useEffect(() => {
    showControlsAndResetTimer();
    return () => { if (controlsTimerRef.current) clearTimeout(controlsTimerRef.current); };
  }, [showControlsAndResetTimer]);

  /* ── Fullscreen listeners ── */
  useEffect(() => {
    const onChange = () => {
      const fs = !!document.fullscreenElement;
      setIsFullscreen(fs);
      if (!fs) {
        setZoomScale(1);
        setZoomOrigin({ x: 50, y: 50 });
        unlockOrientation();
      }
    };
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  const toggleFullscreen = useCallback(async () => {
    const el = videoContainerRef.current;
    if (!el) return;
    try {
      if (!document.fullscreenElement) {
        await el.requestFullscreen();
        await lockLandscape();
      } else {
        await document.exitFullscreen();
        unlockOrientation();
      }
    } catch {
      setIsFullscreen((p) => {
        const next = !p;
        if (next) lockLandscape(); else unlockOrientation();
        return next;
      });
    }
  }, []);

  /* ── Volume timer ── */
  useEffect(() => {
    if (showVolumeSlider) {
      if (volTimerRef.current) clearTimeout(volTimerRef.current);
      volTimerRef.current = setTimeout(() => setShowVolumeSlider(false), 4000);
    }
    return () => { if (volTimerRef.current) clearTimeout(volTimerRef.current); };
  }, [showVolumeSlider, volume]);

  /* ── Container pointer events (video area taps) ── */
  const handleContainerPointerDown = (e: React.PointerEvent) => {
    // Pinch zoom (2 fingers) only in fullscreen
    if (e.pointerType === 'touch') {
      const touches = e.nativeEvent as unknown as { touches?: TouchList };
      if (touches.touches && touches.touches.length === 2 && isFullscreen) {
        const [t1, t2] = [touches.touches[0], touches.touches[1]];
        const dist = Math.sqrt((t2.clientX - t1.clientX) ** 2 + (t2.clientY - t1.clientY) ** 2);
        const rect = videoContainerRef.current?.getBoundingClientRect();
        if (rect) {
          pinchRef.current = {
            initialDist: dist,
            initialScale: zoomScale,
            centerX: ((t1.clientX + t2.clientX) / 2 - rect.left) / rect.width * 100,
            centerY: ((t1.clientY + t2.clientY) / 2 - rect.top) / rect.height * 100,
          };
        }
        return;
      }
    }

    const rect = videoContainerRef.current?.getBoundingClientRect();
    if (!rect) return;
    tapRef.current.x = e.clientX;
    tapRef.current.y = e.clientY;
    tapRef.current.time = Date.now();
    tapRef.current.moved = false;
  };

  const handleContainerPointerMove = (e: React.PointerEvent) => {
    // Pinch handling
    if (pinchRef.current && isFullscreen) {
      const touches = e.nativeEvent as unknown as { touches?: TouchList };
      if (touches.touches && touches.touches.length === 2) {
        const [t1, t2] = [touches.touches[0], touches.touches[1]];
        const dist = Math.sqrt((t2.clientX - t1.clientX) ** 2 + (t2.clientY - t1.clientY) ** 2);
        const ratio = dist / pinchRef.current.initialDist;
        const newScale = Math.min(3, Math.max(1, pinchRef.current.initialScale * ratio));
        setZoomScale(newScale);
        if (newScale > 1) setZoomOrigin({ x: pinchRef.current.centerX, y: pinchRef.current.centerY });
      }
      return;
    }

    const dx = e.clientX - tapRef.current.x;
    const dy = e.clientY - tapRef.current.y;
    if (Math.sqrt(dx * dx + dy * dy) > 10) tapRef.current.moved = true;
  };

  const handleContainerPointerUp = (e: React.PointerEvent) => {
    // Clean up pinch
    if (pinchRef.current) {
      if (zoomScale < 1.1) { setZoomScale(1); setZoomOrigin({ x: 50, y: 50 }); }
      pinchRef.current = null;
      return;
    }

    if (tapRef.current.moved) return;
    const rect = videoContainerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const zone = getTapZone(e.clientX, rect);
    const now = Date.now();
    const isTouch = e.pointerType === 'touch';

    if (zone === 'center') {
      // Close any open menus first
      if (showSettingsRef.current) { setShowSettings(false); showControlsAndResetTimer(); return; }
      if (showVolumeSliderRef.current) { setShowVolumeSlider(false); showControlsAndResetTimer(); return; }

      setIsPlaying((p) => {
        const next = !p;
        showPlayFlash(next ? 'play' : 'pause');
        onPlayStateChange?.(next);
        return next;
      });
      showControlsAndResetTimer();
      return;
    }

    // Left / Right zone
    const dt = tapRef.current.doubleTap;
    const dx = e.clientX - dt.lastX;
    const dy = e.clientY - dt.lastY;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const isDoubleTap = dt.lastZone === zone && now - dt.lastTime < DOUBLE_TAP_MS && dist < DOUBLE_TAP_DISTANCE;

    dt.lastTime = now;
    dt.lastX = e.clientX;
    dt.lastY = e.clientY;
    dt.lastZone = zone;

    if (isDoubleTap && isTouch) {
      const amt = 10;
      if (zone === 'left') {
        setCurrentTime((t) => Math.max(0, t - amt));
        showSeekFlash('left', `-${amt}s`);
      } else {
        setCurrentTime((t) => Math.min(duration, t + amt));
        showSeekFlash('right', `+${amt}s`);
      }
      showControlsAndResetTimer();
      dt.lastTime = 0;
      dt.lastZone = null;
    } else if (!isTouch) {
      // Desktop: single click on edges = seek
      const amt = 10;
      if (zone === 'left') {
        setCurrentTime((t) => Math.max(0, t - amt));
        showSeekFlash('left', `-${amt}s`);
      } else {
        setCurrentTime((t) => Math.min(duration, t + amt));
        showSeekFlash('right', `+${amt}s`);
      }
      showControlsAndResetTimer();
    } else {
      // Touch single tap on edge = show controls only
      showControlsAndResetTimer();
    }
  };

  /* ── Progress bar pointer events ── */
  const handleProgressPointerDown = (e: React.PointerEvent) => {
    e.stopPropagation();
    e.preventDefault();
    const el = e.currentTarget as HTMLDivElement;
    el.setPointerCapture(e.pointerId);
    isDraggingRef.current = true;
    setIsDraggingProgress(true);
    handleSeek(e.clientX, el);
    showControlsAndResetTimer();
  };

  const handleProgressPointerMove = (e: React.PointerEvent) => {
    if (!isDraggingRef.current) {
      // Show tooltip on hover
      const rect = progressBarRef.current?.getBoundingClientRect();
      if (rect) {
        const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
        setSeekTooltip({ visible: true, time: Math.round(pct * duration), x: e.clientX - rect.left });
      }
      return;
    }
    e.stopPropagation();
    e.preventDefault();
    const el = e.currentTarget as HTMLDivElement;
    handleSeek(e.clientX, el);
    const rect = el.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    setSeekTooltip({ visible: true, time: Math.round(pct * duration), x: e.clientX - rect.left });
  };

  const handleProgressPointerUp = (e: React.PointerEvent) => {
    e.stopPropagation();
    isDraggingRef.current = false;
    setIsDraggingProgress(false);
    setSeekTooltip((t) => ({ ...t, visible: false }));
    showControlsAndResetTimer();
  };

  const handleProgressPointerLeave = () => {
    setSeekTooltip((t) => ({ ...t, visible: false }));
  };

  /* ── Outside progress bar (non-fullscreen) ── */
  const handleOutsideProgressDown = (e: React.PointerEvent) => {
    e.stopPropagation();
    e.preventDefault();
    const el = e.currentTarget as HTMLDivElement;
    el.setPointerCapture(e.pointerId);
    isDraggingRef.current = true;
    setIsDraggingProgress(true);
    const rect = el.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    setCurrentTime(pct * duration);
    showControlsAndResetTimer();
  };

  const handleOutsideProgressMove = (e: React.PointerEvent) => {
    if (!isDraggingRef.current) return;
    e.stopPropagation();
    const el = e.currentTarget as HTMLDivElement;
    const rect = el.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    setCurrentTime(pct * duration);
  };

  const handleOutsideProgressUp = (e: React.PointerEvent) => {
    e.stopPropagation();
    isDraggingRef.current = false;
    setIsDraggingProgress(false);
    showControlsAndResetTimer();
  };

  /* ── Wheel zoom (desktop fullscreen) ── */
  const handleWheel = (e: React.WheelEvent) => {
    if (!isFullscreen) return;
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    setZoomScale((s) => {
      const ns = Math.min(3, Math.max(1, s + delta));
      if (ns > 1) {
        const rect = videoContainerRef.current?.getBoundingClientRect();
        if (rect) {
          setZoomOrigin({
            x: ((e.clientX - rect.left) / rect.width) * 100,
            y: ((e.clientY - rect.top) / rect.height) * 100,
          });
        }
      }
      return ns;
    });
  };

  /* ── JSX ── */
  return (
    <div className={`w-full ${isFullscreen ? `fixed inset-0 z-[100] ${t.fullscreenBg}` : ''}`}>
      {/* Video Container */}
      <div
        ref={videoContainerRef}
        className={`relative overflow-hidden select-none ${t.videoBg} ${
          isFullscreen ? 'w-full h-full rounded-none' : 'w-full aspect-video rounded-2xl'
        }`}
        onPointerDown={handleContainerPointerDown}
        onPointerMove={handleContainerPointerMove}
        onPointerUp={handleContainerPointerUp}
        onWheel={handleWheel}
        style={{
          cursor: isFullscreen ? (zoomScale > 1 ? 'grab' : 'pointer') : 'pointer',
          touchAction: 'manipulation',
          WebkitTapHighlightColor: 'transparent',
        }}
      >
        {/* Zoomed video layer */}
        <div
          className="absolute inset-0"
          style={{
            transform: `scale(${zoomScale})`,
            transformOrigin: `${zoomOrigin.x}% ${zoomOrigin.y}%`,
            transition: pinchRef.current ? 'none' : 'transform 0.3s ease-out',
          }}
        >
          <img
            src={lesson.videoThumbnail}
            alt={lesson.title}
            className={`w-full h-full object-cover transition-opacity duration-500 ${isPlaying ? 'opacity-60' : 'opacity-90'}`}
            draggable={false}
          />
        </div>

        {/* Dark overlay */}
        <div className={`absolute inset-0 pointer-events-none ${isFullscreen ? t.overlayFullscreen : t.overlay}`} />

        {/* Play/Pause Flash */}
        {playFlash && (
          <div className="absolute inset-0 flex items-center justify-center z-20 pointer-events-none" style={{ animation: 'playFlashIn 0.18s ease-out forwards' }}>
            <div className={`w-20 h-20 md:w-24 md:h-24 rounded-full ${t.playFlashBg} flex items-center justify-center backdrop-blur-sm`}>
              <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center">
                <i className={`${playFlash === 'play' ? 'ri-play-fill' : 'ri-pause-fill'} ${t.playFlashIcon} text-3xl md:text-4xl`} />
              </div>
            </div>
          </div>
        )}

        {/* Seek Flash */}
        {seekFlash.side && (
          <div
            className={`absolute top-1/2 -translate-y-1/2 pointer-events-none z-20 ${seekFlash.side === 'left' ? 'left-6' : 'right-6'}`}
            style={{ animation: 'seekFlashIn 0.2s ease-out, seekFlashOut 0.5s ease-in 0.2s forwards' }}
          >
            <div className="flex flex-col items-center gap-1">
              <div className={`w-12 h-12 rounded-full ${t.seekFlashBg} border flex items-center justify-center`}>
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className={`${seekFlash.side === 'left' ? 'ri-rewind-start-line' : 'ri-speed-line'} ${t.seekFlashText} text-lg`} />
                </div>
              </div>
              <span className={`${t.seekFlashText} text-xs font-semibold ${t.seekFlashLabelBg} px-2.5 py-1 rounded-full`}>
                {seekFlash.label}
              </span>
            </div>
          </div>
        )}

        {/* Center Play Button (paused, no flash) */}
        {!isPlaying && !playFlash && (
          <div className="absolute inset-0 flex items-center justify-center z-10">
            <button
              onPointerDown={(e) => {
                e.stopPropagation();
                setIsPlaying(true);
                onPlayStateChange?.(true);
                showPlayFlash('play');
                showControlsAndResetTimer();
              }}
              className={`w-16 h-16 md:w-20 md:h-20 rounded-full ${t.playBtnBg} flex items-center justify-center cursor-pointer active:scale-[0.88] transition-transform duration-150`}
            >
              <div className="w-7 h-7 md:w-9 md:h-9 flex items-center justify-center">
                <i className={`ri-play-fill ${t.playBtnIcon} text-xl md:text-2xl ml-0.5`} />
              </div>
            </button>
          </div>
        )}

        {/* ── Top Controls ── */}
        {showControls && (
          <div className="absolute top-0 left-0 right-0 flex items-center justify-between gap-1.5 px-3 pt-3 pb-8 z-20" style={{ animation: 'fadeIn 0.15s ease-out' }}>
            {isFullscreen && (
              <button
                onPointerDown={(e) => { e.stopPropagation(); toggleFullscreen(); }}
                className={`w-11 h-11 rounded-xl ${t.ctrlBg} ${t.ctrlBorder} border flex items-center justify-center cursor-pointer active:scale-90 transition-transform duration-100`}
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`ri-fullscreen-exit-line ${t.ctrlIcon} text-sm`} />
                </div>
              </button>
            )}

            <div className="flex items-center gap-2 ml-auto">
              {/* Volume */}
              <div className="relative">
                <button
                  onPointerDown={(e) => {
                    e.stopPropagation();
                    setIsMuted((m) => !m);
                    setShowVolumeSlider((v) => !v);
                    setShowSettings(false);
                    showControlsAndResetTimer();
                  }}
                  className={`w-11 h-11 rounded-xl ${t.ctrlBg} ${t.ctrlBorder} border flex items-center justify-center cursor-pointer active:scale-90 transition-transform duration-100`}
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i
                      className={`${
                        isMuted
                          ? `ri-volume-mute-line ${t.muteIcon}`
                          : volume > 50
                            ? `ri-volume-up-line ${t.volHigh}`
                            : volume > 0
                              ? `ri-volume-down-line ${t.volLow}`
                              : `ri-volume-mute-line ${t.muteIcon}`
                      } text-sm transition-all duration-150`}
                    />
                  </div>
                </button>

                {showVolumeSlider && (
                  <div
                    className={`absolute top-full mt-2 left-1/2 -translate-x-1/2 ${isDark ? 'bg-[#1C1D21]' : 'bg-white'} ${t.ctrlBorder} border rounded-xl px-3 py-3 w-10 flex flex-col items-center gap-2 z-30 shadow-lg`}
                    style={{ animation: 'fadeIn 0.15s ease-out' }}
                  >
                    <div className={`h-24 w-1 ${t.externalTrack} rounded-full relative overflow-hidden`}>
                      <div className={`absolute bottom-0 left-0 right-0 ${t.progressFill} rounded-full transition-all duration-150`} style={{ height: `${isMuted ? 0 : volume}%` }} />
                    </div>
                    <span className={`${t.textMuted} text-[10px] font-medium`}>{isMuted ? '0' : volume}%</span>
                  </div>
                )}
              </div>

              {/* CC */}
              <button
                onPointerDown={(e) => {
                  e.stopPropagation();
                  setShowCaptions((c) => !c);
                  showControlsAndResetTimer();
                }}
                className={`w-11 h-11 rounded-xl border flex items-center justify-center cursor-pointer active:scale-90 transition-all duration-200 ${
                  showCaptions ? t.ccOn : t.ccOff
                }`}
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <span className="text-[11px] font-bold">CC</span>
                </div>
              </button>

              {/* Settings */}
              <button
                onPointerDown={(e) => {
                  e.stopPropagation();
                  setShowSettings((s) => !s);
                  setShowVolumeSlider(false);
                  showControlsAndResetTimer();
                }}
                className={`w-11 h-11 rounded-xl border flex items-center justify-center cursor-pointer active:scale-90 transition-all duration-200 ${
                  showSettings
                    ? (isDark ? 'bg-white/20 border-white/20 text-white' : 'bg-black/20 border-black/20 text-black')
                    : `${t.ctrlBg} ${t.ctrlBorder} ${t.ctrlText}`
                }`}
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-settings-4-line text-sm" />
                </div>
              </button>

              {/* Fullscreen */}
              <button
                onPointerDown={(e) => { e.stopPropagation(); toggleFullscreen(); }}
                className={`w-11 h-11 rounded-xl ${t.ctrlBg} ${t.ctrlBorder} border flex items-center justify-center cursor-pointer active:scale-90 transition-transform duration-100`}
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`ri-fullscreen-line ${t.ctrlIcon} text-sm`} />
                </div>
              </button>
            </div>
          </div>
        )}

        {/* LAVYN Branding */}
        {showControls && (
          <div className={`absolute bottom-12 right-4 flex items-center gap-1.5 z-10 ${t.brandText}`} style={{ animation: 'fadeIn 0.3s ease-out 0.1s both' }}>
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-sparkling-line text-xs" />
            </div>
            <span className="text-[11px] font-medium tracking-wide">LAVYN Tutor</span>
          </div>
        )}

        {/* Subtitles */}
        {showCaptions && subtitleText && (
          <div className="absolute bottom-16 left-0 right-0 flex items-center justify-center px-8 z-10" style={{ animation: 'fadeIn 0.3s ease-out' }}>
            <p className={`${t.subtitleText} text-sm font-medium text-center leading-relaxed ${t.subtitleBg} px-4 py-2 rounded-xl backdrop-blur-sm`}>
              {subtitleText}
            </p>
          </div>
        )}

        {/* ── Bottom Controls Bar ── */}
        {showControls && (
          <div className={`absolute bottom-0 left-0 right-0 px-3 pb-2 pt-8 ${isFullscreen ? t.bottomBarBgFullscreen : t.bottomBarBg} z-20`} style={{ animation: 'fadeIn 0.15s ease-out' }}>
            {/* Progress Bar */}
            <div
              ref={progressBarRef}
              className={`relative h-1.5 ${t.progressTrack} rounded-full mb-2 cursor-pointer group`}
              onPointerDown={handleProgressPointerDown}
              onPointerMove={handleProgressPointerMove}
              onPointerUp={handleProgressPointerUp}
              onPointerLeave={handleProgressPointerLeave}
            >
              <div className={`absolute top-0 left-0 h-full ${t.progressFill} rounded-full ${isDraggingProgress ? '' : 'transition-all duration-75'}`} style={{ width: `${progress}%` }} />
              <div className={`absolute top-1/2 -translate-y-1/2 w-3.5 h-3.5 ${t.progressFill} rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-150 shadow-lg`} style={{ left: `calc(${progress}% - 7px)` }} />
              <div className={`absolute top-0 left-0 h-full ${t.progressBuffered} rounded-full`} style={{ width: `${Math.min(100, progress + 25)}%` }} />

              {/* Seek tooltip */}
              {seekTooltip.visible && (
                <div
                  className={`absolute -top-8 px-2 py-1 rounded-md ${t.tooltipBg} ${t.ctrlBorder} border text-xs font-medium ${t.textPrimary} whitespace-nowrap shadow-lg`}
                  style={{ left: `${Math.max(0, Math.min(100, (seekTooltip.x / (progressBarRef.current?.getBoundingClientRect().width || 1)) * 100))}%`, transform: 'translateX(-50%)' }}
                >
                  {formatTime(seekTooltip.time)}
                </div>
              )}
            </div>

            {/* Time & Controls Row */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <button
                  onPointerDown={(e) => {
                    e.stopPropagation();
                    togglePlay();
                    showControlsAndResetTimer();
                  }}
                  className={`w-8 h-8 rounded-full ${isDark ? 'bg-white/[0.08] hover:bg-white/[0.15]' : 'bg-black/[0.08] hover:bg-black/[0.15]'} flex items-center justify-center cursor-pointer active:scale-90 transition-transform duration-100`}
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`${isPlaying ? 'ri-pause-fill' : 'ri-play-fill'} ${t.playBtnIcon} text-xs`} />
                  </div>
                </button>
                <span className={`${t.timeText} text-[11px] font-medium tabular-nums`}>
                  {formatTime(currentTime)} / {formatTime(duration)}
                </span>
              </div>
              <span className={`${t.speedText} text-[11px] font-medium`}>{playbackSpeed}x</span>
            </div>
          </div>
        )}
      </div>

      {/* Outside Progress Bar (non-fullscreen) */}
      {!isFullscreen && (
        <div className="mt-3 px-1">
          <div
            className={`relative h-[3px] ${t.externalTrack} rounded-full overflow-hidden cursor-pointer`}
            onPointerDown={handleOutsideProgressDown}
            onPointerMove={handleOutsideProgressMove}
            onPointerUp={handleOutsideProgressUp}
          >
            <div className={`h-full ${t.progressFill} rounded-full ${isDraggingProgress ? '' : 'transition-all duration-150'}`} style={{ width: `${progress}%` }} />
          </div>
        </div>
      )}

      {/* ── Settings Panel ── */}
      {showSettings && (
        <div className="absolute inset-0 z-[110] pointer-events-auto">
          {/* Semi-transparent backdrop */}
          <div
            className="absolute inset-0 bg-black/30"
            onPointerDown={(e) => { e.stopPropagation(); }}
            onPointerUp={(e) => { e.stopPropagation(); setShowSettings(false); }}
          />

          {/* Panel */}
          <div
            className={`absolute ${isFullscreen ? 'right-0 top-0 bottom-0 w-[280px] md:w-[340px]' : 'bottom-0 left-0 right-0'} ${t.sheetBg} overflow-hidden flex flex-col ${isFullscreen ? 'border-l ' + t.settingsBorder : 'rounded-t-3xl'}`}
            style={{ animation: isFullscreen ? 'slideInRight 0.3s cubic-bezier(0.32, 0.72, 0, 1) forwards' : 'slideUp 0.3s cubic-bezier(0.32, 0.72, 0, 1) forwards', maxHeight: isFullscreen ? '100%' : '85vh' }}
            onPointerDown={(e) => e.stopPropagation()}
            onPointerUp={(e) => e.stopPropagation()}
          >
            {!isFullscreen && (
              <div className="flex justify-center pt-3 pb-1" onPointerDown={(e) => e.stopPropagation()} onPointerUp={(e) => { e.stopPropagation(); setShowSettings(false); }}>
                <div className={`w-10 h-1 ${isDark ? 'bg-white/20' : 'bg-black/20'} rounded-full`} />
              </div>
            )}

            {/* Header */}
            <div className="flex items-center justify-between px-5 pt-2 pb-3 flex-shrink-0">
              <h3 className={`${t.textPrimary} text-base font-semibold`}>Settings</h3>
              <button
                onPointerDown={(e) => e.stopPropagation()}
                onPointerUp={(e) => { e.stopPropagation(); setShowSettings(false); }}
                className={`w-9 h-9 rounded-full ${isDark ? 'bg-white/[0.06]' : 'bg-black/[0.06]'} flex items-center justify-center cursor-pointer active:scale-90 transition-transform duration-100`}
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`ri-close-line ${isDark ? 'text-white/60' : 'text-black/60'} text-sm`} />
                </div>
              </button>
            </div>

            {/* Scrollable body */}
            <div className="overflow-y-auto overscroll-contain touch-pan-y flex-1 min-h-0" style={{ WebkitOverflowScrolling: 'touch' }}>
              <div className="px-5 pb-8 pt-2 space-y-5">
                {/* Playback Speed */}
                <div>
                  <p className={`${t.settingsLabel} text-[11px] font-medium uppercase tracking-wider mb-3`}>Playback Speed</p>
                  <div className="flex gap-2">
                    {speedOptions.map((s) => (
                      <button
                        key={s}
                        onPointerDown={(e) => { e.stopPropagation(); setPlaybackSpeed(s); }}
                        className={`flex-1 py-3 rounded-xl text-sm font-semibold transition-all duration-150 cursor-pointer ${
                          playbackSpeed === s ? t.settingsOptionActive : t.settingsOptionInactive
                        }`}
                      >
                        {s}x
                      </button>
                    ))}
                  </div>
                </div>

                {/* Video Quality */}
                <div className={`border-t ${t.settingsBorder} pt-5`}>
                  <p className={`${t.settingsLabel} text-[11px] font-medium uppercase tracking-wider mb-3`}>Video Quality</p>
                  <div className="flex flex-wrap gap-2">
                    {qualityOptions.map((q) => (
                      <button
                        key={q}
                        onPointerDown={(e) => { e.stopPropagation(); setVideoQuality(q); }}
                        className={`px-4 py-2.5 rounded-xl text-sm font-semibold transition-all duration-150 cursor-pointer ${
                          videoQuality === q ? t.settingsOptionActive : t.settingsOptionInactive
                        }`}
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Volume */}
                <div className={`border-t ${t.settingsBorder} pt-5`}>
                  <div className="flex items-center justify-between mb-3">
                    <p className={`${t.settingsLabel} text-[11px] font-medium uppercase tracking-wider`}>Volume</p>
                    <span className={`${t.settingsLabel} text-[11px] font-medium tabular-nums`}>{isMuted ? 0 : volume}%</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onPointerDown={(e) => { e.stopPropagation(); setIsMuted((m) => !m); }}
                      className="w-8 h-8 flex items-center justify-center cursor-pointer flex-shrink-0"
                    >
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className={`${isMuted ? 'ri-volume-mute-line' : volume > 50 ? 'ri-volume-up-line' : volume > 0 ? 'ri-volume-down-line' : 'ri-volume-mute-line'} ${t.settingsIcon} text-sm`} />
                      </div>
                    </button>
                    <div
                      ref={(el) => {
                        if (!el) return;
                        const rect = el.getBoundingClientRect();
                        const handleVolPointer = (e: PointerEvent) => {
                          const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
                          setVolume(Math.round(pct * 100));
                          setIsMuted(false);
                        };
                        const onDown = (e: PointerEvent) => {
                          el.setPointerCapture(e.pointerId);
                          handleVolPointer(e);
                          const onMove = (ev: PointerEvent) => { handleVolPointer(ev); };
                          const onUp = () => { el.removeEventListener('pointermove', onMove); el.removeEventListener('pointerup', onUp); showControlsAndResetTimer(); };
                          el.addEventListener('pointermove', onMove);
                          el.addEventListener('pointerup', onUp);
                        };
                        el.addEventListener('pointerdown', onDown);
                      }}
                      className={`flex-1 h-2 ${t.externalTrack} rounded-full relative overflow-hidden cursor-pointer`}
                    >
                      <div className={`absolute top-0 left-0 h-full ${t.progressFill} rounded-full transition-all duration-75`} style={{ width: `${isMuted ? 0 : volume}%` }} />
                    </div>
                  </div>
                </div>

                {/* AI Voice */}
                <div className={`border-t ${t.settingsBorder} pt-5`}>
                  <p className={`${t.settingsLabel} text-[11px] font-medium uppercase tracking-wider mb-3`}>AI Voice</p>
                  <div className="space-y-1.5">
                    {voiceOptions.map((voice) => (
                      <button
                        key={voice}
                        onPointerDown={(e) => { e.stopPropagation(); setAiVoice(voice); }}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-150 cursor-pointer ${
                          aiVoice === voice ? t.settingsOptionActive : t.settingsOption
                        }`}
                      >
                        <div className="w-5 h-5 flex items-center justify-center">
                          {aiVoice === voice ? (
                            <i className="ri-check-line text-sm" />
                          ) : (
                            <i className={`ri-speak-line text-sm ${t.settingsIconMuted}`} />
                          )}
                        </div>
                        {voice}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Toggles */}
                <div className={`border-t ${t.settingsBorder} pt-5 space-y-4`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className={`ri-closed-captioning-line ${t.settingsIcon} text-sm`} />
                      </div>
                      <span className={`${isDark ? 'text-white/70' : 'text-black/70'} text-sm font-medium`}>Captions</span>
                    </div>
                    <button
                      onPointerDown={(e) => { e.stopPropagation(); setShowCaptions((c) => !c); }}
                      className={`w-12 h-7 rounded-full transition-colors duration-200 cursor-pointer relative ${
                        showCaptions ? t.toggleTrackOn : t.toggleTrackOff
                      }`}
                    >
                      <div
                        className={`absolute top-[3px] w-[22px] h-[22px] rounded-full ${t.toggleThumb} transition-all duration-200 ${
                          showCaptions ? 'left-[23px]' : 'left-[3px]'
                        }`}
                      />
                    </button>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className={`ri-play-circle-line ${t.settingsIcon} text-sm`} />
                      </div>
                      <span className={`${isDark ? 'text-white/70' : 'text-black/70'} text-sm font-medium`}>Auto Play</span>
                    </div>
                    <button
                      onPointerDown={(e) => { e.stopPropagation(); setAutoPlay((a) => !a); }}
                      className={`w-12 h-7 rounded-full transition-colors duration-200 cursor-pointer relative ${
                        autoPlay ? t.toggleTrackOn : t.toggleTrackOff
                      }`}
                    >
                      <div
                        className={`absolute top-[3px] w-[22px] h-[22px] rounded-full ${t.toggleThumb} transition-all duration-200 ${
                          autoPlay ? 'left-[23px]' : 'left-[3px]'
                        }`}
                      />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes seekFlashIn { from { opacity: 0; transform: translateY(-50%) scale(0.6); } to { opacity: 1; transform: translateY(-50%) scale(1); } }
        @keyframes seekFlashOut { from { opacity: 1; transform: translateY(-50%) scale(1); } to { opacity: 0; transform: translateY(-50%) scale(0.6); } }
        @keyframes playFlashIn { from { opacity: 0; transform: scale(0.5); } 40% { opacity: 1; transform: scale(1.08); } 100% { opacity: 0.85; transform: scale(1); } }
        @keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
        @keyframes slideInRight { from { transform: translateX(100%); } to { transform: translateX(0); } }
      `}</style>
    </div>
  );
}